import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  LiveSupportHistory,
  LiveSupportHistoryRow,
  LiveSupportSessionCard,
  SupportDemoContext,
  demoChipTooltip
} from '@features/xatbot/models/xat-bot-live-support.model';
import { LiveSupportService } from '@features/xatbot/services/live-support.service';
import { TellMe } from '@shared/models/TellMe';

/**
 * ITP-E-177-04 (Plan ITP-P-36, stories 11, 12, 14) — the Connection history section of a support ticket (a panel of the
 * ticket's right bar, TaskViewRightBarComponent). It lists every live session's lines — requested, connected, disconnected
 * (by whom, where), reconnected, reconnect requested, closed — with who, where and when (LiveSupportHistoryQuery, for anyone
 * who can read the ticket), and shows the ticket's live chat for its latest session; between sessions that chat is
 * read-only. The server says what the viewer may do now: Disconnect (the connected member, or the client who asked) ends the
 * live chat and never the ticket or its assignment; Reconnect (a support member, once the last session ended) starts a new
 * session, Live at once, on the same chat.
 */
@Component({
  standalone: false,
  selector: 'particle-task-connection-history',
  templateUrl: './task-connection-history.component.html',
  styleUrls: ['./task-connection-history.component.scss']
})
export class TaskConnectionHistoryComponent implements OnChanges, OnDestroy {
  @Input() ticketId: string;
  /** The history the right bar already read to decide this section shows; read again on every change. */
  @Input() history: LiveSupportHistory;

  rows: LiveSupportHistoryRow[] = [];
  latest: LiveSupportSessionCard = null;
  disconnectableSessionId: string = null;
  canReconnect = false;
  busy = false;
  error: string = null;
  readonly myEmail = TellMe.userInfo?.email;

  private readonly destroy$ = new Subject<void>();

  constructor(private liveSupportService: LiveSupportService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['history'] && this.history) {
      this.show(this.history);
    } else if (changes['ticketId'] && this.ticketId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** ITP-E-233-06 — the demo the ticket's sessions were asked from; null outside a demo. */
  get demoContext(): SupportDemoContext | null {
    const sessions = this.history?.sessions ?? [];
    return (this.latest?.demoContext ?? sessions.find(session => session.demoContext?.isDemo)?.demoContext) ?? null;
  }

  get demoTooltip(): string {
    return demoChipTooltip(this.demoContext);
  }

  get canDisconnect(): boolean {
    return !!this.disconnectableSessionId && !this.busy;
  }

  /** "Disconnected · Dana Smith · via ITP". */
  describe(row: LiveSupportHistoryRow): string {
    return [row.kind, row.actor, row.via ? `via ${row.via}` : null, row.detail].filter(part => !!part).join(' · ');
  }

  load(): void {
    if (!this.ticketId) {
      return;
    }
    this.liveSupportService.history(this.ticketId).pipe(takeUntil(this.destroy$)).subscribe(history => this.show(history));
  }

  disconnect(): void {
    if (!this.canDisconnect) {
      return;
    }
    this.busy = true;
    this.error = null;
    this.liveSupportService.disconnect(this.disconnectableSessionId).pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.busy = false;
      if (result.error && result.session?.state !== 'Closed') {
        this.error = result.error;
      }
      this.load();
    });
  }

  reconnect(): void {
    if (!this.canReconnect || this.busy) {
      return;
    }
    this.busy = true;
    this.error = null;
    this.liveSupportService.reconnect(this.ticketId).pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.busy = false;
      // "You're in a live chat (SUP-12)…", "Answered by Dana Smith just now." — the server's words.
      this.error = result.error;
      this.load();
    });
  }

  /** The chat moved (a message aside): disconnected, answered, or a later session of the ticket. The section reads again. */
  onSessionChange(session: LiveSupportSessionCard): void {
    if (!session) {
      return;
    }
    if (session.sessionId !== this.latest?.sessionId || session.state !== this.latest?.state || session.version !== this.latest?.version) {
      this.load();
    }
  }

  private show(history: LiveSupportHistory): void {
    if (history.error && !history.rows?.length) {
      this.error = history.error;
      return;
    }
    this.rows = history.rows ?? [];
    const sessions = history.sessions ?? [];
    this.latest = sessions.length ? sessions[sessions.length - 1] : null;
    this.disconnectableSessionId = history.disconnectableSessionId;
    this.canReconnect = history.canReconnect;
  }
}
